
// ============================================
// NewsScene - ニュースページシーン
// ============================================

/**
 * GNS.NewsScene
 * * ニュース一覧ページ（/news/）のメイン制御クラス
 * * 機能:
 * - ニュース一覧の表示（初回はHTML、追加分はAPIから取得）
 * - "More"ボタンによる追加読み込み
 * - スクロールによる自動追加読み込み（プログレスバー演出付き）
 * - サムネイルと矢印の連動インタラクション
 */
GNS.NewsScene = function (GNS) {
    // AbstractSceneを継承
    const scene = GNS.create(GNS.AbstractScene);

    /**
     * 初期化処理
     */
    scene.init = function (data) {
        scene._super.init.call(this, data);

        // ステート管理
        this.page = 1;
        this.totalPage = 1;
        this.isLoading = false;

        // UI要素の取得
        this.container = new GNS.DisplayObject(this.element.querySelector(".news__container"));
        this.btnMore = new GNS.DisplayObject("news__btn_more");
        this.btnMoreShadow = new GNS.DisplayObject("news__btn_more__shadow"); // プログレスバー用

        // ボタンの状態管理用配列（Resize/Scroll等の管理用）
        this.buttons = [];

        // Moreボタンクリックイベント
        if (this.btnMore.element) {
            this.btnMore.addEvent("click", this.loadMore);
        }

        // 自動読み込みアニメーション用変数
        this.moreProgress = 0;
        this.isTimerRunning = false;
        this.timerId = null;

        // タッチコントローラー（スワイプ判定用）
        this.touchController = new GNS.TouchActionController(window);
        this.touchController.isPreventDefault = false;
    };

    /**
     * ロード処理
     */
    scene.load = function () {
        // 既存の要素があるかチェック（初回ロード時）
        const existingItems = this.container.element.querySelectorAll('.news__container__line');

        if (this.page === 1 && existingItems.length > 0) {
            // data属性から総ページ数を取得
            const totalPageAttr = this.container.element.getAttribute('data-total-page');
            this.totalPage = totalPageAttr ? parseInt(totalPageAttr, 10) : 1;

            // 既存アイテムをフェードイン対象に追加
            Array.from(existingItems).forEach(item => {
                this.addSceneEventIn(item);
            });

            this._updateMoreButtonState();
            this._loadComplete(); // ロード完了処理へ（ここでボタン初期化を行う）

        } else {
            // APIからデータを取得
            this.isLoading = true;
            GNS.APINews.dispatcher.addEventListener(GNS.Event.COMPLETE, this._onApiComplete);
            GNS.APINews.request(this.page);
        }
    };

    /**
     * APIデータロード完了時の処理
     */
    scene._bind._onApiComplete = function (event) {
        GNS.APINews.dispatcher.removeEventListener(GNS.Event.COMPLETE, this._onApiComplete);

        if (event && event.params && event.params.news) {
            this.totalPage = event.params.total_page;
            const newsList = event.params.news;

            // ニュースリストのHTML生成とDOM追加
            this._renderNewsList(newsList);

            this._updateMoreButtonState();
        }

        this._loadComplete();
    };

    /**
     * ニュースリストのレンダリング
     * 3件ずつ1行（.news__container__line）にまとめる
     */
    scene._renderNewsList = function (newsList) {
        const wrapperDiv = document.createElement("div");
        wrapperDiv.className = `_page${this.page}`;

        let htmlContent = "";

        // 3件ごとのチャンク処理
        for (let i = 0; i < newsList.length; i += 3) {
            // 現在の行に表示するデータ（最大3件）
            const chunk = newsList.slice(i, i + 3);
            // インデックス情報の配列（ID生成用: [originalIndex, originalIndex+1, ...]）
            const indices = chunk.map((_, idx) => i + idx);

            htmlContent += '<div class="news__container__line">';

            // 1. 左側の矢印リンク群
            htmlContent += '<div class="news__container__line__arror">';
            chunk.forEach((item, idx) => {
                const globalIndex = indices[idx];
                const uniqueId = `${globalIndex}_${this.page}`;
                htmlContent += this._createArrowHtml(item, uniqueId);
            });
            htmlContent += '</div>';

            // 2. 右側の詳細ボックス群
            chunk.forEach((item, idx) => {
                const globalIndex = indices[idx];
                const uniqueId = `${globalIndex}_${this.page}`;
                htmlContent += this._createBoxHtml(item, uniqueId);
            });

            htmlContent += '</div>'; // end line
        }

        wrapperDiv.innerHTML = htmlContent;
        this.container.element.appendChild(wrapperDiv);

        // 生成した要素をフェードイン対象に登録
        this.addSceneEventIn(wrapperDiv);

        // 新しく生成された要素に対してボタン機能を初期化
        this._initNewButtons(wrapperDiv, newsList.length);
    };

    /**
     * 矢印リンクHTML生成テンプレート
     */
    scene._createArrowHtml = function (item, idSuffix) {
        const imgPath = GNS.APIModel.IMAGE_PATH;
        return `
      <div id="item__link${idSuffix}" class="item__link">
        <a href="${item.url}">
          <div class="item__link__view">
            <div class="item__link__view__bartop"></div>
            <div class="item__link__view__barright"></div>
            <div class="item__link__view__barbottom"></div>
            <img class="item__link__view__img img_pc" src="${imgPath}common/link_arror_pc.png" />
            <img class="item__link__view__img img_sp" src="${imgPath}common/link_arror_sp.png" />
          </div>
          <div class="item__link__bg"></div>
        </a>
      </div>
    `;
    };

    /**
     * 記事ボックスHTML生成テンプレート
     */
    scene._createBoxHtml = function (item, idSuffix) {
        const imgPath = GNS.APIModel.IMAGE_PATH;
        return `
      <div id="news__container__line__box${idSuffix}" class="news__container__line__box">
        <div class="news__container__line__box__column">
          <div class="news__container__line__box__column__img">
            <img id="news__container__line__box__column__img${idSuffix}" src="${item.image}" />
          </div>
          <div class="news__container__line__box__column__text">
            <div class="news__container__line__box__column__text__date">
              <div class="news__container__line__box__column__text__date__tag">
                <p>${item.date}</p>
              </div>
              <div class="news__container__line__box__column__text__date__bar"></div>
            </div>
            <p class="news__container__line__box__column__text__title lang_jp">${item.title_jp}</p>
            <p class="news__container__line__box__column__text__title lang_en">${item.title_en}</p>
          </div>
        </div>
        <div class="news__container__line__box__overview lang_jp">${item.desc_jp}</div>
        <div class="news__container__line__box__overview lang_en">${item.desc_en}</div>
        
        <div id="item__link__sp${idSuffix}" class="item__link">
          <a href="${item.url}">
            <div class="item__link__view">
              <div class="item__link__view__bartop"></div>
              <div class="item__link__view__barright"></div>
              <div class="item__link__view__barbottom"></div>
              <img class="item__link__view__img img_pc" src="${imgPath}common/link_arror_pc.png" />
              <img class="item__link__view__img img_sp" src="${imgPath}common/link_arror_sp.png" />
            </div>
            <div class="item__link__bg"></div>
          </a>
        </div>
      </div>
    `;
    };

    /**
     * Moreボタンの表示状態更新
     */
    scene._updateMoreButtonState = function () {
        if (this.page < this.totalPage) {
            this.btnMore.addClass("show");
        } else {
            this.btnMore.removeClass("show");
        }
    };

    /**
     * API経由で生成された要素のボタン初期化
     */
    scene._initNewButtons = function (wrapper, count) {
        // API経由の場合、IDが付与されているのでIDベースで取得可能
        for (let i = 0; i < count; i++) {
            const uniqueId = `${i}_${this.page}`;

            const imgObj = new GNS.DisplayObject(`news__container__line__box__column__img${uniqueId}`);
            const boxObj = new GNS.DisplayObject(`news__container__line__box${uniqueId}`);
            const spLinkObj = new GNS.DisplayObject(`item__link__sp${uniqueId}`);

            // PC/SP共通の矢印リンク
            // UIThumBtn(TriggerLink, Bg, Image, BoxContainer, ExtraLink, Delay)
            const btn = new GNS.UIThumBtn(
                `item__link${uniqueId}`, // ID文字列またはElementを渡せる想定
                null,
                imgObj,
                boxObj,
                spLinkObj,
                0.05 * i
            );

            this.buttons.push(btn);
        }
    };

    /**
     * ロード完了時（共通）
     * PHPで生成された既存要素のボタン初期化を行う
     */
    scene._bind._loadComplete = function () {
        // ページ1（初回ロード）の場合、DOMから要素を探してボタン化する
        // ※APIロードの場合は _initNewButtons で処理済みだが、ここはSSR用の処理
        if (this.page === 1) {
            this._initExistingButtons();
        }

        this.isLoading = false;
        scene._super.load.call(this);
    };

    /**
     * 既存DOM（PHP生成分）のボタン初期化
     */
    scene._initExistingButtons = function () {
        const lines = this.container.element.querySelectorAll('.news__container__line');

        Array.from(lines).forEach((line, lineIndex) => {
            const boxes = line.querySelectorAll('.news__container__line__box');

            Array.from(boxes).forEach((boxEl, boxIndex) => {
                // 画像
                const imgEl = boxEl.querySelector('.news__container__line__box__column__img'); // imgタグそのものではなくラッパーの場合あり、必要なら imgEl.querySelector('img')
                const imgObj = imgEl ? new GNS.DisplayObject(imgEl) : null;

                // 矢印リンク（左側）: 親要素のラインの中から、同じインデックスのものを探す
                const arrowLinkEl = line.querySelector(`.news__container__line__arror .item__link:nth-child(${boxIndex + 1})`);

                // SP用リンク（記事内）
                const spLinkEl = boxEl.querySelector('.item__link');
                const spLinkObj = spLinkEl ? new GNS.DisplayObject(spLinkEl) : null;

                const boxObj = new GNS.DisplayObject(boxEl);

                if (arrowLinkEl) {
                    const btn = new GNS.UIThumBtn(
                        arrowLinkEl,
                        null,
                        imgObj,
                        boxObj,
                        spLinkObj,
                        0.05 * (lineIndex * 3 + boxIndex)
                    );
                    this.buttons.push(btn);
                }
            });
        });
    };

    /**
     * 表示ハンドラ
     */
    scene.showHandler = function () {
        this.moreProgress = 0;

        // イベント登録
        this.touchController.addEventListener(GNS.TouchActionEvent.TOUCH_1_MOVE, this.dragging);
        GNS.DOM.addEvent(window, "scroll", this.scrollCheck);

        // ホイールイベントのクロスブラウザ対応
        const wheelEventName = ("onwheel" in document) ? "wheel" :
            ("onmousewheel" in document) ? "mousewheel" : "DOMMouseScroll";
        this.element.addEventListener(wheelEventName, this.onMouseWheelHandler, { passive: true });
    };

    /**
     * 非表示ハンドラ
     */
    scene.hideHandler = function () {
        this.touchController.removeEventListener(GNS.TouchActionEvent.TOUCH_1_MOVE, this.dragging);
        GNS.DOM.removeEvent(window, "scroll", this.scrollCheck);

        const wheelEventName = ("onwheel" in document) ? "wheel" :
            ("onmousewheel" in document) ? "mousewheel" : "DOMMouseScroll";
        this.element.removeEventListener(wheelEventName, this.onMouseWheelHandler);

        this._stopMoreTimer();
    };

    /**
     * リセットハンドラ
     */
    scene.resetHandler = function () {
        this.buttons.forEach(btn => {
            if (btn.kill) btn.kill();
        });
        this.buttons = [];
    };

    /**
     * Moreボタンクリックハンドラ
     */
    scene._bind.loadMore = function () {
        if (!this.isLoading) {
            this.page++;
            this.load();
        }
    };

    // ----------------------------------------------------------------
    // Scroll & Auto Load Logic
    // ----------------------------------------------------------------

    /**
     * スクロール位置のチェック
     */
    scene._bind.scrollCheck = function () {
        if (!this.isLoading && this.page < this.totalPage) {
            const pageSize = GNS.DOM.getPageSize();
            const threshold = 100; // ページ下部から100px以内

            // ページ最下部に近いか判定
            const isNearBottom = (pageSize.scrollY + pageSize.displayHeight) >= (pageSize.stageHeight - threshold);

            if (isNearBottom) {
                // 下部に到達したら、タイマーアニメーションを開始して自動読み込みを促す
                this.moreProgress = 16; // 閾値(15)を超える値をセットして即時反応させる
                this._startMoreTimer();
            }
        }
    };

    /**
     * マウスホイールハンドラ
     */
    scene._bind.onMouseWheelHandler = function (e) {
        if (!e) e = window.event;

        // ホイールの方向を判定 (正: 下スクロール, 負: 上スクロール)
        const delta = e.deltaY ? -(e.deltaY) : (e.wheelDelta ? e.wheelDelta : -(e.detail));

        // 下スクロール時のみチェック
        if (delta < 0) {
            this.scrollCheck();
        }
    };

    /**
     * タッチドラッグハンドラ
     */
    scene._bind.dragging = function () {
        // 上にスワイプ（画面が下に移動）している量を計算
        const deltaY = -(this.touchController.touch0CurrentY - this.touchController.touch0PrevY);

        // 一定以上スワイプしたらチェック
        if (deltaY > 20) {
            this.scrollCheck();
        }
    };

    /**
     * 自動読み込み用タイマー開始
     */
    scene._bind._startMoreTimer = function () {
        if (!this.isTimerRunning) {
            this.isTimerRunning = true;
            this.timerId = setInterval(this.moreTimeUpdate, 16); // ~60fps
        }
    };

    /**
     * 自動読み込み用タイマー停止
     */
    scene._bind._stopMoreTimer = function () {
        if (this.isTimerRunning) {
            clearInterval(this.timerId);
            this.isTimerRunning = false;
            this.timerId = null;
        }
    };

    /**
     * タイマー更新処理（アニメーションとトリガー）
     */
    scene._bind.moreTimeUpdate = function () {
        // 値を減衰させる（慣性のような動き）
        this.moreProgress *= 0.995;

        // プログレスバー（影）の高さを計算 (0〜15の範囲を0〜100%にマッピング)
        const progressPercent = GNS.Animate.easeOutQuart(Math.min(this.moreProgress, 15) / 15, 0, 100, 1);
        this.btnMoreShadow.element.style.height = `${progressPercent}%`;

        if (this.moreProgress < 0.5) {
            // 値が小さくなりすぎたら停止（リセット）
            this.moreProgress = 0;
            this._stopMoreTimer();
            this.btnMoreShadow.element.style.height = "0%";

        } else if (this.moreProgress > 15) {
            // 閾値を超えたらロード実行
            this.moreProgress = 0;
            this._stopMoreTimer();
            this.btnMoreShadow.element.style.height = "0%";
            this.loadMore();
        }
    };

    return scene._self;
}(GNS);
